<template>
  <div>
    <v-dialog
    scrollable
    :width="width"
    :max-width="maxWidth"
    persistent
    :fullscreen="fullscreen"
    :content-class="contentClass"
    v-model="show"
    @click:outside="handleCloseRequest">
      <slot v-if="show"></slot>
    </v-dialog>
    <v-dialog v-if="confirmClose" v-model="showingConfirmClose" max-width="400px" persistent>
      <ConfirmDialog
        v-if="showingConfirmClose"
        :title="confirmCloseTitle || $t('closeDialogWithoutSavingTitle')"
        :body="confirmCloseBody || $t('closeDialogWithoutSavingWarning')"
        :confirmText="$t('close')"
        @cancel="onCancelClose"
        @confirm="onConfirmClose"
      />
    </v-dialog>
  </div>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex'
import store from '@/store/index.js'
import DialogState from '../model/DialogState.js'
export default {
  name: 'Dialog',

  components: {
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue')
  },

  props: {
    maxWidth: {
      type: String,
      default: undefined,
      required: false
    },
    fullscreenBreakPoint: {
      type: String,
      default: 'mdAndDown',
      required: false
    },
    stateId: {
      type: String,
      default: undefined
    },
    contentClass: {
      type: String,
      default: undefined,
      required: false
    },
    enforcePersistence: {
      type: Boolean,
      default: false,
      required: false
    },
    confirmClose: {
      type: Boolean,
      default: false,
      required: false
    },
    confirmCloseTitle: {
      type: String,
      default: undefined,
      required: false
    },
    confirmCloseBody: {
      type: String,
      default: undefined,
      required: false
    }
  },

  data: () => ({
    stateObject: {
      type: DialogState,
      default: undefined
    },
    show: false,
    showingConfirmClose: false
  }),

  created () {
    this.stateObject = undefined
    this.show = false
    if (this.topDialogId === this.stateId) {
      this.stateObject = this.topDialogState
      this.stateObject.requiresConfirmClose = this.confirmClose
      this.show = true
    }
    store.subscribe((mutation) => {
      if (
        (mutation.type === 'dialog/pushDialog' || mutation.type === 'dialog/pushOrSetDialog') &&
        mutation.payload.id === this.stateId
      ) {
        this.stateObject = mutation.payload
        this.stateObject.requiresConfirmClose = this.confirmClose
        this.show = true
      }
    })

    document.addEventListener('keydown', this.keyListener)
  },

  beforeDestroy () {
    this.show = false
    document.removeEventListener('keydown', this.keyListener)
    this.popAllDialogsAtAndAbove(this.stateId)
  },

  computed: {
    ...mapGetters('dialog', ['dialogCount', 'dialogStateById', 'hasOpenDialogById', 'topDialogId', 'topDialogState', 'allowEscapeToClose', 'hasConfirmCloseDialogAbove', 'isTopDialogById']),
    width () {
      return this.stateObject?.width ?? '400px'
    },
    fullscreen () {
      return (
        this.stateObject?.allowFullscreen &&
        (this.stateObject?.fullscreen ||
        this.$vuetify.breakpoint[this.fullscreenBreakPoint])
      ) ?? false
    },
    closeRequested () {
      return this.stateObject?.closeRequested ?? false
    }
  },

  watch: {
    closeRequested (val) {
      if (val) {
        this.handleCloseRequest()
      }
    },
    confirmClose (val) {
      if (this.stateObject) {
        this.stateObject.requiresConfirmClose = val
      }
    }
  },

  methods: {
    ...mapMutations('dialog', ['popDialog', 'popAllDialogsAtAndAbove']),

    keyListener (event) {
      // Every mounted Dialog hears this document-level event, so only the top of the stack may act on it.
      if (event.keyCode === 27 && this.allowEscapeToClose && this.show && !this.showingConfirmClose && this.isTopDialogById(this.stateId)) {
        if (this.hasConfirmCloseDialogAbove(this.stateId)) return
        this.handleCloseRequest(event)
      }
    },

    close () {
      this.$emit('dialog-closing')
    },

    handleCloseRequest (event) {
      if (this.confirmClose) {
        if (this.stateObject) this.stateObject.closeRequested = false
        this.showingConfirmClose = true
        return
      }
      if (this.enforcePersistence) {
        if (this.stateObject) this.stateObject.closeRequested = false
        return
      }
      this.$emit('click:outside', event)
      this.doClose()
    },

    onConfirmClose () {
      this.showingConfirmClose = false
      this.doClose()
    },

    onCancelClose () {
      this.showingConfirmClose = false
    },

    doClose () {
      if (this.stateObject) this.stateObject.closeRequested = false
      this.popDialog()
      this.$emit('close')
      this.stateObject = undefined
      this.show = false
    }
  }
}
</script>
